import { ChartType } from '@sisense/sdk-ui';
import { z } from 'zod';
import { chartType } from '../types';

const DashStyleSchema = z.enum([
	'Solid',
	'ShortDash',
	'ShortDot',
	'ShortDashDot',
	'ShortDashDotDot',
	'Dot',
	'Dash',
	'LongDash',
	'DashDot',
	'LongDashDot',
	'LongDashDotDot',
]);

const EndCapTypeSchema = z.enum(['Round', 'Square']);

const LineOptionsSchema = z
	.object({
		width: z
			.number()
			.optional()
			.describe('Width of the line in pixels (LineOptions.width).'),
		dashStyle: DashStyleSchema.optional().describe(
			'Dash pattern for the line (LineOptions.dashStyle).',
		),
		endCap: EndCapTypeSchema.optional().describe(
			'Line end cap style (LineOptions.endCap).',
		),
		shadow: z
			.boolean()
			.optional()
			.describe('Whether to apply a shadow effect to the line.'),
	})
	.strict()
	.describe('Line drawing style for line and area charts (LineOptions).');

const LineSubtypeSchema = z.enum(['line/basic', 'line/spline', 'line/step']);

const LegendItemsSchema = z
	.object({
		layout: z
			.enum(['horizontal', 'vertical', 'proximate'])
			.optional()
			.describe(
				'Layout direction for legend items (replaces sole use of deprecated `position`).',
			),
	})
	.strict();

// Common components schemas
const LegendSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if legend should be shown on the chart',
			),
		align: z
			.enum(['left', 'center', 'right'])
			.optional()
			.describe(
				'Horizontal alignment of the legend (LegendOptions.align).',
			),
		verticalAlign: z
			.enum(['top', 'middle', 'bottom'])
			.optional()
			.describe(
				'Vertical alignment of the legend (LegendOptions.verticalAlign).',
			),
		items: LegendItemsSchema.optional().describe(
			'Legend item layout and spacing (LegendOptions.items).',
		),
	})
	.describe(
		'Options that define legend — a key for data series or colors (LegendOptions subset).',
	)
	.strict();

const SeriesLabelsTextStyleSchema = z
	.object({
		fontSize: z
			.union([z.string(), z.number()])
			.optional()
			.describe(
				'Font size for label text (CSS or number as accepted by TextStyle).',
			),
		fontWeight: z
			.string()
			.optional()
			.describe('Font weight for label text.'),
		color: z
			.union([z.literal('contrast'), z.string()])
			.optional()
			.describe(
				'Label text color, or `contrast` for maximum contrast (SeriesLabelsTextStyle.color).',
			),
	})
	.strict();

const SeriesLabelsSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if series labels (data labels identifying the series) should be shown on the chart.',
			),
		showValue: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if the numeric value should be shown in series labels (default depends on chart type if omitted).',
			),
		showPercentage: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if percentage should be shown in series labels (for subtypes that support percentages, e.g. stacked100).',
			),
		textStyle: SeriesLabelsTextStyleSchema.optional().describe(
			'Typography for series labels (SeriesLabelsBase.textStyle / SeriesLabelsTextStyle).',
		),
	})
	.describe(
		'Configuration for series labels — titles or values identifying data series or points on the chart (subset of SeriesLabels).',
	)
	.strict();
// Create separate axis schemas instead of reusing
const AxisLabel = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if this axis should be shown on the chart.',
			),
		title: z
			.object({
				enabled: z
					.boolean()
					.default(true)
					.optional()
					.describe(
						'Boolean flag that defines if the axis title should be shown.',
					),
				text: z
					.string()
					.optional()
					.describe('Textual content of the axis title.'),
			})
			.optional()
			.describe(
				'Configuration for the axis title (not specific to X or Y).',
			),
		min: z
			.number()
			.nullable()
			.optional()
			.describe(
				'Minimum axis value; use null or omit to let the chart compute the minimum automatically.',
			),
		max: z
			.number()
			.nullable()
			.optional()
			.describe(
				'Maximum axis value; use null or omit to let the chart compute the maximum automatically.',
			),
		gridLines: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if grid lines should be shown on the chart',
			),
		intervalJumps: z
			.number()
			.optional()
			.describe('Interval of the tick marks (jumps) in axis units.'),
		isIntervalEnabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if tick marks should be shown on the axis',
			),
		labels: z
			.object({
				enabled: z
					.boolean()
					.optional()
					.describe(
						'Boolean flag that defines if tick labels should be shown on the axis.',
					),
				showValue: z
					.boolean()
					.optional()
					.describe(
						'If supported for this chart, whether numeric values appear on axis tick labels (beyond AxisLabel.labels.enabled).',
					),
				showPercentage: z
					.boolean()
					.optional()
					.describe(
						'If supported for this chart, whether percentages appear on axis tick labels (beyond AxisLabel.labels.enabled).',
					),
			})
			.optional()
			.describe('Configuration for tick labels on the axis.'),
		logarithmic: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if the axis should have logarithmic scale',
			),
		templateMainYHasGridLines: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if the Y axis should have grid lines extending the ticks across the plot area',
			),
	})
	.strict();

const XAxisSchema = AxisLabel.describe('X-axis configuration');

const YAxisSchema = AxisLabel.describe('Y-axis configuration');

const Y2AxisSchema = AxisLabel.describe('Y2-axis configuration');

const MarkersSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if markers should be shown on the chart',
			),
		fill: z
			.enum(['filled', 'hollow'])
			.optional()
			.describe('Marker fill style'),
		size: z
			.enum(['small', 'medium', 'large'])
			.optional()
			.describe(
				'Marker size preset. Typical values are small or large; medium is accepted here for compatibility.',
			),
	})
	.describe(
		'Options that define markers - symbols or data points that highlight specific values.',
	)
	.strict();

const NavigatorSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if navigator should be shown on the chart',
			),
		scrollerLocation: z
			.object({
				max: z
					.number()
					.optional()
					.describe('The maximum value of the navigator scroller'),
				min: z
					.number()
					.optional()
					.describe('The minimum value of the navigator scroller'),
			})
			.optional()
			.describe(
				'The scroll location of the navigator scroller / auto zoom feature',
			),
	})
	.describe(
		'Options that define navigator - zoom/pan tool for large datasets in a chart.',
	)
	.strict();

const DataLimitsSchema = z
	.object({
		categoriesCapacity: z
			.number()
			.optional()
			.describe('Maximum number of categories to chart'),
		seriesCapacity: z
			.number()
			.optional()
			.describe('Maximum number of series to chart'),
	})
	.describe(
		'Configuration that limits the series or categories that are charted.',
	)
	.strict();

const TotalLabelsSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Whether total labels are shown at each stacked point (stacked bar/column/area only).',
			),
		rotation: z
			.number()
			.optional()
			.describe('Rotation of total labels in degrees.'),
		align: z
			.enum(['left', 'center', 'right'])
			.optional()
			.describe('Horizontal alignment of the total label vs the point.'),
		verticalAlign: z
			.enum(['top', 'middle', 'bottom'])
			.optional()
			.describe('Vertical alignment of the total label vs the point.'),
	})
	.strict()
	.describe('Total labels on stacked charts (TotalLabels subset).');

const StackableSeriesSchema = z
	.object({
		padding: z
			.number()
			.optional()
			.describe(
				'Padding between each column or bar, in x axis units (default 0.01).',
			),
		groupPadding: z
			.number()
			.optional()
			.describe(
				'Padding between each value group, in x axis units (default 0.1).',
			),
		borderRadius: z
			.union([z.number(), z.string()])
			.optional()
			.describe(
				'Corner radius of each column or bar: pixels as number, or percentage string (e.g. 50%).',
			),
	})
	.strict()
	.describe('Per-series bar/column geometry (StackableStyleOptions.series).');

// Chart-specific schemas
export const LineChartStyleSchema = z
	.object({
		line: LineOptionsSchema.optional().describe(
			'Line stroke style including width in pixels (replaces deprecated preset `lineWidth`).',
		),
		stepPosition: z
			.enum(['left', 'center', 'right'])
			.optional()
			.describe(
				'For subtype line/step: where the step occurs relative to each point (LineStyleOptions.stepPosition).',
			),
		subtype: LineSubtypeSchema.optional().describe(
			'Crucial complement to chartType for line charts: straight segments between points (line/basic); curved segments between points (line/spline); step-like horizontal and vertical segments between points (line/step).',
		),
		markers: MarkersSchema.optional().describe(
			'Markers at data points (symbols highlighting values along the line).',
		),
		navigator: NavigatorSchema.optional().describe(
			'Mini-map scroller for zooming and panning large series along the category axis.',
		),
		dataLimits: DataLimitsSchema.optional().describe(
			'Caps on how many categories or series are plotted.',
		),
		legend: LegendSchema.optional().describe(
			'Legend for series names and colors.',
		),
		xAxis: XAxisSchema.optional().describe(
			'Primary category or continuous X-axis configuration.',
		),
		yAxis: YAxisSchema.optional().describe(
			'Primary value Y-axis configuration.',
		),
		y2Axis: Y2AxisSchema.optional().describe(
			'Secondary Y-axis configuration (e.g. for a second measure scale).',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Data labels associated with each series (values, percentages, etc.).',
		),
	})
	.strict()
	.describe('Style options for line charts (LineStyleOptions subset).');

export const BarChartStyleSchema = z
	.object({
		subtype: z
			.enum(['bar/classic', 'bar/stacked', 'bar/stacked100'])
			.optional()
			.describe(
				'Crucial complement to chartType for bar charts: classic layout with y-axis–mapped values in the default non-stacked form (bar/classic); y-axis values stacked next to each other without overlapping (bar/stacked); stacks combined and stretched so the bar represents 100% (bar/stacked100).',
			),
		markers: MarkersSchema.optional().describe(
			'Markers on bar series when applicable (e.g. line overlays in mixed charts).',
		),
		navigator: NavigatorSchema.optional().describe(
			'Navigator for panning/zooming many categories.',
		),
		legend: LegendSchema.optional().describe(
			'Legend for series or break-by colors.',
		),
		xAxis: XAxisSchema.optional().describe(
			'Category or continuous axis along which bars are laid out.',
		),
		yAxis: YAxisSchema.optional().describe(
			'Primary measure axis (bar lengths).',
		),
		y2Axis: Y2AxisSchema.optional().describe(
			'Secondary measure axis when dual-axis bars or mixed series.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on or near bar data points.',
		),
		totalLabels: TotalLabelsSchema.optional().describe(
			'Totals on stacked bar subtypes (StackableStyleOptions.totalLabels).',
		),
		series: StackableSeriesSchema.optional().describe(
			'Padding and corner radius for bar series (StackableStyleOptions.series).',
		),
	})
	.strict()
	.describe(
		'Style options for horizontal bar charts (StackableStyleOptions subtype family).',
	);

export const ColumnChartStyleSchema = z
	.object({
		subtype: z
			.enum([
				'column/classic',
				'column/stackedcolumn',
				'column/stackedcolumn100',
			])
			.optional()
			.describe(
				'Crucial complement to chartType for column charts: columns shown side by side (column/classic); columns stacked vertically with no overlap (column/stackedcolumn); stacked columns normalized so combined height represents 100% (column/stackedcolumn100).',
			),
		markers: MarkersSchema.optional().describe(
			'Markers on column series when applicable.',
		),
		navigator: NavigatorSchema.optional().describe(
			'Navigator for panning/zooming many categories.',
		),
		legend: LegendSchema.optional().describe(
			'Legend for series or break-by colors.',
		),
		xAxis: XAxisSchema.optional().describe(
			'Category axis under the columns.',
		),
		yAxis: YAxisSchema.optional().describe(
			'Primary value axis (column heights).',
		),
		y2Axis: Y2AxisSchema.optional().describe(
			'Secondary value axis for dual-axis or mixed charts.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on or near column tops.',
		),
		totalLabels: TotalLabelsSchema.optional().describe(
			'Totals on stacked column subtypes (StackableStyleOptions.totalLabels).',
		),
		series: StackableSeriesSchema.optional().describe(
			'Padding and corner radius for column series (StackableStyleOptions.series).',
		),
	})
	.strict()
	.describe(
		'Style options for vertical column charts (StackableStyleOptions subtype family).',
	);

const ConvolutionSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that defines if convolution ability should be enabled on chart',
			),
		independentSlicesCount: z
			.number()
			.optional()
			.describe(
				'Number that defines of how many independent slices should be present in chart (other will be convolved)',
			),
		minimalIndependentSlicePercentage: z
			.number()
			.optional()
			.describe(
				'Value that defines what minimal size in percent should a slice take to fall under the convolution',
			),
		selectedConvolutionType: z
			.enum(['byPercentage', 'bySlicesCount'])
			.optional()
			.describe('Selected type of convolution behavior'),
	})
	.describe(
		'Configuration that defines the ability of the Pie chart to collapse (convolve) and hide part of the data under the single category "Others".',
	)
	.strict();

export const PieChartStyleSchema = z
	.object({
		subtype: z
			.enum(['pie/classic', 'pie/donut', 'pie/ring'])
			.optional()
			.describe(
				'Crucial complement to chartType for pie charts: a circle of slices from the center, one segment per category (pie/classic); same slices with the center removed for a donut (pie/donut); same with the center removed for a ring look (pie/ring).',
			),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Slice or series labels (values, categories, percentage labels; PieSeriesLabels).',
		),
		legend: LegendSchema.optional().describe(
			'Legend mapping colors to categories or series.',
		),
		convolution: ConvolutionSchema.optional().describe(
			'Collapses small slices into an "Others" bucket (Convolution).',
		),
	})
	.strict()
	.describe('Style options for pie charts (PieStyleOptions subset).');

export const ScatterChartStyleSchema = z
	.object({
		markers: MarkersSchema.optional().describe(
			'Point marker appearance (filled/hollow and size preset) for scatter points.',
		),
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on how many points or series are drawn for performance.',
		),
		markerSize: z

			.object({
				scatterBubbleMaxSize: z
					.number()
					.optional()
					.describe('Maximum size of markers'),
				scatterBubbleMinSize: z
					.number()
					.optional()
					.describe('Minimum size of markers'),
				scatterDefaultSize: z
					.number()
					.optional()
					.describe('Default size of markers'),
			})

			.optional()

			.describe(
				'Numeric bubble/point size range when a size measure is used (ScatterMarkerSize).',
			),
		legend: LegendSchema.optional().describe(
			'Legend for series or color encoding.',
		),
		xAxis: XAxisSchema.optional().describe(
			'X-axis configuration for the scatter plane.',
		),
		yAxis: YAxisSchema.optional().describe(
			'Y-axis configuration for the scatter plane.',
		),
		y2Axis: Y2AxisSchema.optional().describe(
			'Optional second Y-axis for dual-scale scatter.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on scatter points or series.',
		),
	})
	.strict()
	.describe(
		'Style options for scatter / bubble charts (ScatterStyleOptions subset).',
	);

export const AreaChartStyleSchema = z
	.object({
		subtype: z
			.enum([
				'area/basic',
				'area/spline',
				'area/stacked',
				'area/stacked100',
				'area/stackedspline',
				'area/stackedspline100',
			])
			.optional()
			.describe(
				'Crucial complement to chartType for area charts: overlapping areas with straight lines (area/basic) or splines (area/spline), smaller series drawn on top; stacked non-overlapping areas with straight lines (area/stacked) or splines (area/stackedspline); same stacks normalized to 100% height (area/stacked100, area/stackedspline100).',
			),
		opacity: z
			.number()
			.optional()
			.describe(
				'Fill opacity of the area layers (0–1 typical; lower is more transparent).',
			),
		line: LineOptionsSchema.optional().describe(
			'Stroke style for area boundaries (AreaStyleOptions.line; use `line.width` in pixels instead of deprecated preset lineWidth).',
		),
		markers: MarkersSchema.optional().describe(
			'Markers on area series when applicable (BaseAxisStyleOptions.markers).',
		),
		totalLabels: TotalLabelsSchema.optional().describe(
			'Totals on stacked area subtypes (AreaStyleOptions.totalLabels).',
		),
		navigator: NavigatorSchema.optional().describe(
			'Navigator for long category axes on area charts.',
		),
		legend: LegendSchema.optional().describe(
			'Legend for series or stacks.',
		),
		xAxis: XAxisSchema.optional().describe(
			'Category or continuous axis under the areas.',
		),
		yAxis: YAxisSchema.optional().describe('Primary stacked value axis.'),
		y2Axis: Y2AxisSchema.optional().describe(
			'Secondary value axis when needed.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on area points or stacks.',
		),
	})
	.strict()
	.describe('Style options for area charts (AreaStyleOptions subset).');

export const FunnelChartStyleSchema = z
	.object({
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on categories or series included in the funnel.',
		),
		funnelDirection: z
			.enum(['inverted', 'regular'])
			.optional()
			.describe('Direction of FunnelChart narrowing'),
		funnelSize: z
			.enum(['narrow', 'regular', 'wide'])
			.optional()
			.describe(
				'Visual size of the lowest slice (degree of funnel narrowing from highest to lowest slices)',
			),
		funnelType: z
			.enum(['pinched', 'regular'])
			.optional()
			.describe(
				'Visual type of the lowest slice of the funnel (e.g. pinched vs regular neck).',
			),
		subtype: z
			.enum(['funnel/basic', 'funnel/pyramid'])
			.optional()
			.describe(
				'funnel/basic: standard funnel; funnel/pyramid: triangular pyramid layout.',
			),
		neckHeight: z
			.number()
			.optional()
			.describe(
				'Height of the funnel neck (narrow end) in design units; rendered as percentage of plot in the chart engine.',
			),
		neckWidth: z
			.number()
			.optional()
			.describe(
				'Width of the funnel neck (narrow end) in design units; often expressed as a percentage of funnel width in rendering.',
			),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on funnel segments (FunnelSeriesLabels may include showCategory).',
		),
		legend: LegendSchema.optional().describe(
			'Legend for funnel stages or series.',
		),
	})
	.strict()
	.describe(
		'Style options for funnel charts (FunnelStyleOptions plus neck geometry used by the funnel plot).',
	);

export const TreemapChartStyleSchema = z
	.object({
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on how many hierarchy nodes or series are shown.',
		),
		layout: z
			.enum([
				'squarified',
				'stripes',
				'stripes-vertical',
				'stripes-horizontal',
			])
			.optional()
			.describe(
				'Treemap tiling algorithm: squarified balances aspect ratios; stripes variants lay out slices in horizontal or vertical strips.',
			),
		borderWidth: z
			.number()
			.optional()
			.describe(
				'Border width in pixels between treemap cells or levels.',
			),
		borderColor: z
			.string()
			.optional()
			.describe('CSS color for borders between treemap cells.'),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on treemap tiles (per-level arrays supported in TreemapSeriesLabels).',
		),
		legend: LegendSchema.optional().describe(
			'Legend when color encodes an additional breakdown.',
		),
		tooltip: z
			.object({
				mode: z
					.enum(['contribution', 'value'])
					.optional()
					.describe(
						'Tooltip content mode: value shows raw values; contribution shows share of parent or total.',
					),
			})
			.optional()
			.describe(
				'Tooltip behavior for treemap nodes (TreemapStyleOptions.tooltip).',
			),
	})
	.strict()
	.describe(
		'Style options for treemap charts (extends public TreemapStyleOptions with layout and border tuning).',
	);

export const SunburstChartStyleSchema = z
	.object({
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on how many hierarchy levels or slices are displayed.',
		),
		levels: z
			.number()
			.optional()
			.describe(
				'Number of concentric hierarchy levels to emphasize or style in the sunburst.',
			),
		borderWidth: z
			.number()
			.optional()
			.describe(
				'Border width in pixels between sunburst rings or segments.',
			),
		borderColor: z
			.string()
			.optional()
			.describe('CSS color for borders between sunburst segments.'),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on sunburst arcs (SunburstSeriesLabels; supports border and background styling).',
		),
		legend: LegendSchema.optional().describe(
			'Legend for hierarchy levels or color encoding.',
		),
	})
	.strict()
	.describe(
		'Style options for sunburst charts (SunburstStyleOptions subset plus layout borders).',
	);

export const BoxplotChartStyleSchema = z
	.object({
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on categories or series rendered as box plots.',
		),
		subtype: z
			.enum(['boxplot/full', 'boxplot/hollow'])
			.optional()
			.describe(
				'Visual subtype: full box with median and whiskers, or hollow box emphasizing the IQR box outline.',
			),
		boxType: z
			.enum(['iqr', 'extremums', 'standardDeviation'])
			.optional()
			.describe(
				'Algorithm for whiskers and box edges (matches BoxplotChartDataOptions.boxType / BoxWhiskerType).',
			),
		outliersEnabled: z
			.boolean()
			.optional()
			.describe(
				'Whether outlier points are drawn beyond whiskers (also on BoxplotChartDataOptions).',
			),
		width: z
			.number()
			.optional()
			.describe('Pixel width of each box along the category axis.'),
		legend: LegendSchema.optional().describe(
			'Legend for series or break-by dimensions.',
		),
		xAxis: XAxisSchema.optional().describe(
			'Category axis (one box group per category).',
		),
		yAxis: YAxisSchema.optional().describe(
			'Value axis for box-and-whisker metrics.',
		),
		y2Axis: Y2AxisSchema.optional().describe('Optional second value axis.'),
		markers: MarkersSchema.optional().describe(
			'Markers used for outlier points when enabled.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on boxes, medians, or outliers.',
		),
	})
	.strict()
	.describe(
		'Style and presentation options for boxplot charts (mixes BoxplotStyleOptions with data-option fields used in editors).',
	);

export const IndicatorChartStyleSchema = z
	.object({
		subtype: z
			.enum(['indicator/numeric', 'indicator/gauge'])
			.optional()
			.describe(
				'indicator/numeric: KPI number layout; indicator/gauge: arc or bar gauge (GaugeIndicatorStyleOptions).',
			),
		value: z
			.number()
			.optional()
			.describe(
				'Displayed or design-time main value for gauge-style previews (live data comes from dataOptions.value measures).',
			),
		min: z
			.number()
			.optional()
			.describe(
				'Lower bound of the gauge or numeric scale (see dataOptions.min measures).',
			),
		max: z
			.number()
			.optional()
			.describe(
				'Upper bound of the gauge or numeric scale (see dataOptions.max measures).',
			),
		thresholds: z
			.array(z.number())
			.optional()
			.describe(
				'Optional ordered numeric thresholds for gauge color bands or ticks.',
			),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on the indicator value or secondary values when shown.',
		),
		legend: LegendSchema.optional().describe(
			'Rarely used; legend when multiple thresholds or series are shown.',
		),
		forceTickerView: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag to force render indicator in ticker mode regardless of display size',
			),
		skin: z
			.enum(['1', '2'])
			.optional()
			.describe(
				'Gauge skin preset (1 or 2) for indicator/gauge subtype (GaugeIndicatorStyleOptions.skin).',
			),
	})
	.strict()
	.describe(
		'Style options for KPI / indicator charts (IndicatorStyleOptions subset plus gauge bounds).',
	);

export const PolarChartStyleSchema = z
	.object({
		dataLimits: DataLimitsSchema.optional().describe(
			'Limits on categories or series drawn in the polar plot.',
		),
		height: z
			.number()
			.optional()
			.describe(
				'Widget height in pixels (falls back to container or default when omitted, see BaseStyleOptions.height).',
			),
		width: z
			.number()
			.optional()
			.describe(
				'Widget width in pixels (falls back to container or default when omitted, see BaseStyleOptions.width).',
			),
		legend: LegendSchema.optional().describe(
			'Legend for polar series or categories.',
		),
		markers: MarkersSchema.optional().describe(
			'Markers on polar line or area points.',
		),
		navigator: NavigatorSchema.optional().describe(
			'Navigator when the angular axis spans many points.',
		),
		subtype: z
			.enum(['polar/column', 'polar/area', 'polar/line'])
			.optional()
			.describe(
				'Crucial complement to chartType for polar charts: values plotted by angle around the center and distance from it (polar/column); points connected by a line with the enclosed polar sector filled (polar/area); points connected by a line only (polar/line).',
			),
		xAxis: XAxisSchema.optional().describe(
			'Angular or radial axis configuration depending on chart mapping.',
		),
		yAxis: YAxisSchema.optional().describe(
			'Radial value axis configuration.',
		),
		y2Axis: Y2AxisSchema.optional().describe(
			'Secondary radial axis when dual-scale polar charts are used.',
		),
		seriesLabels: SeriesLabelsSchema.optional().describe(
			'Labels on polar sectors, columns, or lines.',
		),
	})
	.strict()
	.describe('Style options for polar charts (PolarStyleOptions subset).');

const TableAlternatingColorSchema = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether to fill with color (TableColorOptions.enabled).',
			),
		textColor: z
			.string()
			.optional()
			.describe(
				'Color used for text in the styled region (TableColorOptions.textColor).',
			),
		backgroundColor: z
			.string()
			.optional()
			.describe(
				'Color used for background fill (TableColorOptions.backgroundColor).',
			),
	})
	.describe('Alternating or header color styling for table cells.');

const TableChartStyleSchema = z
	.object({
		columns: z
			.object({
				alternatingColor:
					TableAlternatingColorSchema.optional().describe(
						'Alternating column background and text colors.',
					),
				width: z
					.enum(['auto', 'content'])
					.optional()
					.describe(
						"Column width mode: 'auto' equalizes columns to fit the table width; 'content' sizes by cell content (default).",
					),
			})
			.optional()
			.describe('Configuration for columns'),
		header: z
			.object({
				color: TableAlternatingColorSchema.optional().describe(
					'Header cell fill and text colors (TableStyleOptions.header.color).',
				),
			})
			.optional()
			.describe('Configuration for header'),
		rows: z
			.object({
				alternatingColor:
					TableAlternatingColorSchema.optional().describe(
						'Alternating row background and text colors.',
					),
			})
			.optional()
			.describe('Configuration for rows'),
		rowsPerPage: z.number().optional().describe('Number of rows per page'),
		paddingHorizontal: z
			.number()
			.optional()
			.describe('Horizontal padding around whole table'),
		paddingVertical: z
			.number()
			.optional()
			.describe('Vertical padding around whole table'),
	})
	.strict()
	.describe(
		'Style options for the table widget (TableStyleOptions / TabularChartStyleOptions).',
	);

export const chartStyleSchemas: Record<ChartType, z.ZodObject<any>> = {
	line: LineChartStyleSchema,
	bar: BarChartStyleSchema,
	area: AreaChartStyleSchema,
	column: ColumnChartStyleSchema,
	pie: PieChartStyleSchema,
	scatter: ScatterChartStyleSchema,
	funnel: FunnelChartStyleSchema,
	treemap: TreemapChartStyleSchema,
	sunburst: SunburstChartStyleSchema,
	boxplot: BoxplotChartStyleSchema,
	indicator: IndicatorChartStyleSchema,
	polar: PolarChartStyleSchema,
	table: TableChartStyleSchema,
	arearange: z
		.object({})
		.describe(
			'Area range style options (AreaRangeStyleOptions: line, subtype, seriesLabels, axes); this editing schema does not expose fields yet.',
		),
	scattermap: z
		.object({})
		.describe(
			'Scattermap uses ScattermapStyleOptions (markers fill/size, width/height); not exposed in this editing schema yet.',
		),
	areamap: z
		.object({})
		.describe(
			'Areamap uses AreamapStyleOptions (mapType world|usa, width/height); not exposed in this editing schema yet.',
		),
	'calendar-heatmap': z
		.object({})
		.describe(
			'Calendar heatmap uses CalendarHeatmapStyleOptions (subtype, viewType, cellLabels, etc.); not exposed in this editing schema yet.',
		),
	streamgraph: z
		.object({})
		.describe(
			'Streamgraph uses StreamgraphStyleOptions (line, seriesLabels, axes, etc.); not exposed in this editing schema yet.',
		),
} as const;

const baseChartSchema = z.object({
	chartType,
});

// Function to get schema by chart type
export function getChartStyleOptionsSchema(chartType: ChartType) {
	const styleSchema = chartStyleSchemas[chartType];

	if (!styleSchema) {
		throw new Error(`Unsupported chart type: ${chartType}`);
	}

	return baseChartSchema.extend({
		styleOptions: styleSchema,
	});
}

// Type for all chart style options
export type ChartStyleOptions =
	| z.infer<typeof LineChartStyleSchema>
	| z.infer<typeof BarChartStyleSchema>
	| z.infer<typeof ColumnChartStyleSchema>
	| z.infer<typeof PieChartStyleSchema>
	| z.infer<typeof ScatterChartStyleSchema>
	| z.infer<typeof AreaChartStyleSchema>
	| z.infer<typeof FunnelChartStyleSchema>
	| z.infer<typeof TreemapChartStyleSchema>
	| z.infer<typeof SunburstChartStyleSchema>
	| z.infer<typeof BoxplotChartStyleSchema>
	| z.infer<typeof IndicatorChartStyleSchema>;

type DataOptionTypes =
	| 'cartesian'
	| 'categorical'
	| 'scatter'
	| 'table'
	| 'indicator'
	| 'areamap'
	| 'arearange';

const dataOptionsSchemas: Record<DataOptionTypes, z.ZodObject<any>> = {
	cartesian: z.object({
		category: z.array(
			z.object({
				column: z.object({}),
			}),
		),
		value: z.array(
			z.object({
				column: z.object({}),
			}),
		),
	}),
	categorical: z.object({
		category: z.array(z.object({})),
		value: z.array(z.object({})),
	}),
	scatter: z.object({
		x: z.object({}),
		y: z.object({}),
	}),
	table: z.object({}),
	indicator: z.object({}),
	areamap: z.object({}),
	arearange: z.object({}),
} as const;

export const getChartDataOptionsSchema = (dataOptionType: DataOptionTypes) => {
	return dataOptionsSchemas[dataOptionType];
};

export const PartialColumnSchema = z.object({
	name: z
		.string()
		.describe(
			'Column or field identifier (matches Column.name in the data model).',
		),
	title: z
		.string()
		.describe('Human-readable title shown in the UI for the column.'),
});

export const ColorStringSchema = z
	.string()
	.describe('hex color code for the measure.');
export const UniformColorSchema = z
	.object({
		color: ColorStringSchema,
		type: z
			.enum(['uniform'])
			.describe('Type of color to be used for the measure.'),
	})
	.describe(
		'Uniform color options for data similar to the Single Color option in the Sisense UI.',
	);

export const ConditionalColorSchema = z
	.object({
		conditions: z
			.array(
				z.object({
					color: ColorStringSchema,
					expression: z.string(),
					operator: z.enum(['<', '>', '>=', '<=']),
				}),
			)
			.describe('Array of color conditions'),
		defaultColor: ColorStringSchema.describe(
			'Default color when no condition is met',
		),
		type: z.enum(['conditional']).describe('Color options type'),
	})
	.describe(
		'Conditional color options for data similar to the Conditional Color option in the Sisense UI.',
	);

export const ColorOptionsSchema = z.object({
	color: z.string().describe('hex color code for the measure.'),
});

export const RangeColorSchema = z
	.object({
		maxColor: ColorStringSchema.describe(
			'End color of the range',
		).optional(),
		maxValue: z
			.number()
			.describe(
				'Maximum value explicitly set to override the maximum value of the data',
			)
			.optional(),
		midValue: z
			.number()
			.describe(
				'Middle value explicitly set to override the middle value of the data',
			)
			.optional(),
		minColor: ColorStringSchema.describe(
			'Start color of the range',
		).optional(),
		minValue: z
			.number()
			.describe(
				'Minimum value explicitly set to override the minimum value of the data',
			)
			.optional(),
		steps: z
			.number()
			.describe('Distinct number of colors in the range')
			.optional(),
		type: z.enum(['range']).describe('Color options type'),
	})
	.describe('Color options for range color.');

export const NumberFormatConfigSchema = z
	.object({
		billion: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether to show an abbreviation for a number greater than or equal one billion - e.g. 1B',
			),
		decimalScale: z
			.union([z.number(), z.literal('auto')])
			.optional()
			.describe(
				'Number of decimal places, or "auto" to let the formatter decide (DecimalScale).',
			),
		kilo: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether to show an abbreviation for a number greater than or equal one thousand - e.g. 1K',
			),
		million: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether to show an abbreviation for a number greater than or equal one million - e.g. 1M',
			),
		name: z
			.enum(['Currency', 'Numbers', 'Percent'])
			.optional()
			.describe('Supported formats'),
		prefix: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether symbol is shown in front of or after the number',
			),
		symbol: z
			.string()
			.optional()
			.describe(
				'Symbol to show in front of or after the number depending on the value of prefix.',
			),
		thousandSeparator: z
			.boolean()
			.optional()
			.describe('Boolean flag whether the thousand separator is shown'),
		trillion: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag whether to show an abbreviation for a number greater than or equal one trillion - e.g. 1T',
			),
	})
	.describe('NumberFormatConfig: formatting for measures and axis labels.');

export const trendFormulaOptions = z
	.object({
		ignoreAnomalies: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag that enables the function to automatically identify and ignore anomalous values in the series. This can be particularly useful when you want to maintain the integrity of your analysis by avoiding potential outliers. default is false',
			),
		modelType: z
			.enum([
				'advancedSmoothing',
				'linear',
				'localEstimates',
				'logarithmic',
			])
			.optional()
			.describe(
				'Trend analysis model type to be used for the operation. default is linear',
			),
	})
	.describe('Trend analysis options for the measure');

export const forecastFormulaOptions = z
	.object({
		confidenceInterval: z
			.number()
			.optional()
			.describe(
				'Confidence interval showing the probabilistic upper and lower limits of the forecasted series according to the uncertainty level. The valid range is (0.8 <= X < 1). default is 0.8',
			),
		endDate: z
			.string()
			.optional()
			.describe(
				'End date of the time series data that the forecasting model will be trained on. This parameter can be used to discard the end of the series. Specify a ISO 8601 date string or Date object.',
			),
		forecastHorizon: z
			.number()
			.optional()
			.describe(
				'Number of data points to be predicted. The accepted value range is between 1-1,000. default is 3',
			),
		lowerBound: z
			.number()
			.optional()
			.describe(
				'Expected lower limit to improve the forecast accuracy when reaching the limit. Note that values in the confidence interval can exceed this limit.',
			),
		modelType: z
			.enum(['auto', 'autoArima', 'holtWinters', 'prophet'])
			.optional()
			.describe(
				"Forecasting model type. The 'auto' option automatically fits the best combination of models. default is auto",
			),
		roundToInt: z
			.boolean()
			.optional()
			.describe(
				'Boolean flag to round the predicted result to an integer if set to true. Otherwise, the predicted result is left as a float. default is false',
			),
		startDate: z
			.string()
			.optional()
			.describe(
				'Start date of the time series data that the forecasting model will be trained on. This parameter can be used to discard the beginning of the series. Specify a ISO 8601 date string or Date object.',
			),
		upperBound: z
			.number()
			.optional()
			.describe(
				'Expected upper limit to improve the forecast accuracy when reaching the limit. Note that values in the confidence interval can exceed this limit.',
			),
	})
	.describe('Forecast analysis options for the measure');

export const dataOptionsEditingSchemas = {
	measures: z
		.object({
			column: PartialColumnSchema.describe(
				'Identifies the underlying measure column (StyledMeasureColumn.column).',
			),
			chartType: z
				.enum(['line', 'bar', 'column', 'area'])
				.describe(
					'Series chart type, which is used with StyledMeasure to customize series in a mixed chart.',
				)
				.optional(),
			sortType: z
				.enum(['sortAsc', 'sortDesc', 'sortNone'])
				.describe(
					'Sorting direction, either in Ascending order, Descending order, or None',
				),
			color: z.string().describe('hex color code for the measure.'),
			connectNulls: z
				.boolean()
				.describe(
					'Boolean flag whether to connect a graph line across null points or render a gap',
				),
			dataBars: z
				.boolean()
				.describe(
					'pivot table only -Boolean flag whether to display data bars for this measure in the pivot table.',
				),
			dataBarsColor: z
				.string()
				.describe(
					'pivot table only - Color options for data bars for this measure in the pivot table',
				),
			numberFormatConfig: NumberFormatConfigSchema.describe(
				'Configuration for number formatting.',
			),
			seriesStyleOptions: z
				.object({})
				.describe(
					'Per-series overrides (SeriesStyleOptions): lineWidth (LineWidth preset thin|bold|thick), markers. Chart-level line stroke uses styleOptions.line on line/area charts.',
				),
			showOnRightAxis: z
				.boolean()
				.describe(
					'Boolean flag whether to show this value/measure on the right axis (true) or on the left axis (false).',
				),
			totalsCalculation: z
				.enum(['sum', 'avg', 'min', 'max', 'count'])
				.describe(
					'Calculation for the totals of this measure in the pivot table.',
				),
			treatNullDataAsZeros: z
				.boolean()
				.describe(
					'Boolean flag whether null values are treated as zeros in the chart',
				),
			trend: trendFormulaOptions
				.optional()
				.describe('Trend analysis options for the measure.'),
			forecast: forecastFormulaOptions
				.optional()
				.describe('Forecast analysis options for the measure.'),
		})
		.describe('Configuration for measures.StyledMeasure'),
	dimensions: z
		.object({
			column: PartialColumnSchema.describe(
				'Identifies the underlying dimension column (StyledColumn.column).',
			),
			continuous: z
				.boolean()
				.describe(
					'Boolean flag to toggle continuous timeline on this date column.',
				),
			dateFormat: z
				.string()
				.describe(
					'Date format. See ECMAScript Date Time String Format(opens new window) Note that "YYYY" and "DD" have been disabled since they often get confused with "yyyy" and "dd" and can produce unexpected results.',
				),
			geoLevel: z
				.enum([
					'country',
					'state',
					'city',
					'zip',
					'latitude',
					'longitude',
				])
				.describe('Geographic location level for Scattermap chart.'),
			granularity: z
				.enum(['day', 'week', 'month', 'year'])
				.describe(
					'Date granularity that works with continuous timeline. Values from DateLevels.',
				),
			includeSubTotals: z
				.boolean()
				.describe(
					'Boolean flag whether to include subtotals for this dimension in the pivot table.',
				),
			isColored: z
				.boolean()
				.describe('Boolean flag whether to color the dimension.'),
			isHtml: z
				.boolean()
				.describe(
					'Boolean flag whether to render category/column value as HTML in the Table component.',
				),
			numberFormatConfig: NumberFormatConfigSchema.describe(
				'Configuration for number formatting.',
			),
			sortType: z
				.enum(['sortAsc', 'sortDesc', 'sortNone'])
				.describe(
					'Sorting configuration that represents either SortDirection or PivotRowsSort for the pivot table',
				),
		})
		.describe('Configuration for dimensions. StyledColumn'),
};

// export type DataOptionsEditingSchemas = z.infer<typeof dataOptionsEditingSchemas>;

export const cartesianDataOptionsEditingSchemas = z
	.object({
		value: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures plotted on the Y-axis (or polar radius), each may be StyledMeasureColumn.',
			),
		category: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Categories on the X-axis (or angle axis), StyledColumn entries.',
			),
		breakBy: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Optional break-by dimension(s) for series grouping and coloring.',
			),
		seriesToColorMap: z
			.object({})
			.describe(
				'Optional mapping of each of the series to colors. (key: color)',
			),
	})
	.describe(
		'CartesianChartDataOptions subset: category, value, breakBy, seriesToColorMap.',
	);

export const categoricalDataOptionsEditingSchemas = z
	.object({
		value: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures defining slice or segment sizes (pie, funnel, treemap, sunburst).',
			),
		category: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe('Dimensions defining categories or hierarchy levels.'),
		seriesToColorMap: z
			.object({})
			.describe(
				'Optional mapping of each of the series to colors. (key: color)',
			),
	})
	.describe(
		'CategoricalChartDataOptions subset for pie, funnel, treemap, and sunburst.',
	);
export const scatterChartDataOptionsEditingSchemas = z
	.object({
		y: dataOptionsEditingSchemas.measures.describe(
			'Measure or column for the vertical axis (ScatterChartDataOptions.y).',
		),
		x: dataOptionsEditingSchemas.measures.describe(
			'Measure or column for the horizontal axis (ScatterChartDataOptions.x).',
		),
		breakByColor: dataOptionsEditingSchemas.measures.describe(
			'Optional measure used to encode point color (break-by color encoding).',
		),
		breakByPoint: dataOptionsEditingSchemas.dimensions.describe(
			'Descriptive column placing one point per member (ScatterChartDataOptions.breakByPoint).',
		),
		size: dataOptionsEditingSchemas.measures.describe(
			'Optional measure controlling bubble size (ScatterChartDataOptions.size).',
		),
		seriesToColorMap: z
			.object({})
			.describe(
				'Optional mapping of each of the series to colors. (key: color)',
			),
	})
	.describe('ScatterChartDataOptions editing shape.');

export const indicatorDataOptionsEditingSchemas = z
	.object({
		min: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures used for the minimum bound of a gauge (IndicatorChartDataOptions.min).',
			),
		max: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures used for the maximum bound of a gauge (IndicatorChartDataOptions.max).',
			),
		secondary: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures for a secondary KPI value (IndicatorChartDataOptions.secondary).',
			),
		value: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measures for the primary KPI value (IndicatorChartDataOptions.value).',
			),
	})
	.describe('IndicatorChartDataOptions editing shape.');

export const boxplotDataOptionsEditingSchemas = z
	.object({
		boxType: z
			.enum(['extremums', 'iqr', 'standardDeviation'])
			.describe(
				'Box whisker algorithm: iqr, extremums, or standardDeviation (BoxWhiskerType).',
			),
		category: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Optional category column grouping boxes (BoxplotChartDataOptions.category).',
			),
		outliersEnabled: z
			.boolean()
			.optional()
			.describe(
				'Whether to render outlier points beyond whiskers (BoxplotChartDataOptions.outliersEnabled).',
			),
		value: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Single numeric measure whose distribution drives box plot statistics (BoxplotChartDataOptions.value).',
			),
	})
	.describe('BoxplotChartDataOptions editing shape.');

export const scattermapDataOptionsEditingSchemas = z
	.object({
		colorBy: dataOptionsEditingSchemas.measures.describe(
			'Measure controlling point color (ScattermapChartDataOptions.colorBy).',
		),
		// todo: or statement dimensions or measures
		details: dataOptionsEditingSchemas.dimensions.describe(
			'Dimension or measure column for tooltip detail rows (ScattermapChartDataOptions.details).',
		),
		geo: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Geographic columns (country, state, city, or lat/long pair in order) for point placement (ScattermapChartDataOptions.geo).',
			),
		size: dataOptionsEditingSchemas.measures.describe(
			'Optional measure controlling marker size (ScattermapChartDataOptions.size).',
		),
	})
	.describe('ScattermapChartDataOptions editing shape.');

export const areamapDataOptionsEditingSchemas = z
	.object({
		color: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measure(s) driving region fill color (AreamapChartDataOptions.color).',
			),
		geo: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Geographic dimension for regions on the map (AreamapChartDataOptions.geo).',
			),
	})
	.describe('AreamapChartDataOptions editing shape.');

export const calendarHeatmapDataOptionsEditingSchemas = z
	.object({
		date: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Date dimension placed on the calendar grid (CalendarHeatmapChartDataOptions.date).',
			),
		value: z
			.array(dataOptionsEditingSchemas.measures)
			.describe(
				'Measure shown as cell color intensity (CalendarHeatmapChartDataOptions.value).',
			),
	})
	.describe('CalendarHeatmapChartDataOptions editing shape.');

export const tableDataOptionsEditingSchemas = z
	.object({
		columns: z
			.array(dataOptionsEditingSchemas.dimensions)
			.describe(
				'Table columns as styled dimensions (TableDataOptions.columns).',
			),
	})
	.describe('TableDataOptions editing shape.');

export const getChartDataOptionsEditingSchemas = (chartType: ChartType) => {
	switch (chartType) {
		case 'line':
		case 'bar':
		case 'column':
		case 'area':
		case 'polar':
			return cartesianDataOptionsEditingSchemas;
		case 'pie':
		case 'funnel':
		case 'treemap':
		case 'sunburst':
			return categoricalDataOptionsEditingSchemas;
		case 'scatter':
			return scatterChartDataOptionsEditingSchemas;
		case 'indicator':
			return indicatorDataOptionsEditingSchemas;
		case 'table':
			return tableDataOptionsEditingSchemas;
		case 'boxplot':
			return boxplotDataOptionsEditingSchemas;
		case 'scattermap':
			return scattermapDataOptionsEditingSchemas;
		case 'areamap':
			return areamapDataOptionsEditingSchemas;
		case 'calendar-heatmap':
			return calendarHeatmapDataOptionsEditingSchemas;
		default:
			return z
				.object({})
				.describe(
					'No structured data-option schema for this chart type in this module; refer to ChartDataOptions for the full shape.',
				);
	}
};

export const chartDataOptionsSchemas: Record<ChartType, z.ZodObject<any>> = {
	line: cartesianDataOptionsEditingSchemas,
	bar: cartesianDataOptionsEditingSchemas,
	area: cartesianDataOptionsEditingSchemas,
	column: cartesianDataOptionsEditingSchemas, // Assuming column is similar to bar; adjust as needed
	pie: categoricalDataOptionsEditingSchemas,
	scatter: scatterChartDataOptionsEditingSchemas,
	funnel: categoricalDataOptionsEditingSchemas,
	treemap: categoricalDataOptionsEditingSchemas,
	sunburst: categoricalDataOptionsEditingSchemas,
	boxplot: boxplotDataOptionsEditingSchemas,
	indicator: indicatorDataOptionsEditingSchemas,
	polar: cartesianDataOptionsEditingSchemas,
	table: tableDataOptionsEditingSchemas,
	arearange: z
		.object({})
		.describe(
			'Area range uses RangeChartDataOptions; structured editing schema not defined here.',
		),
	scattermap: scattermapDataOptionsEditingSchemas,
	areamap: areamapDataOptionsEditingSchemas,
	'calendar-heatmap': calendarHeatmapDataOptionsEditingSchemas,
	streamgraph: z
		.object({})
		.describe(
			'Streamgraph shares cartesian-style data options; use line/area family data options or extend this record when modeled.',
		),
} as const;
